import { createFeatureSelector, createSelector } from '@ngrx/store';
import { filter } from 'lodash';

import { Role, User } from '@lockton-apps/shared/interfaces';
import { UserRouterSelectors } from '@lockton-apps/shared/data-access/router';

import { USERS_FEATURE_KEY, usersAdapter, UsersState } from './users.reducer';
import { AuthSelectors } from '@lockton-apps/shared/data-access/auth';

// Lookup the 'Users' feature state managed by NgRx
export const getUsersState =
  createFeatureSelector<UsersState>(USERS_FEATURE_KEY);

const { selectAll, selectEntities } = usersAdapter.getSelectors();

export const getLoaded = createSelector(
  getUsersState,
  (state: UsersState) => state.loaded
);

export const getMutationsLoading = createSelector(
  getUsersState,
  (state: UsersState) => state.mutationsLoading
);

export const getUsersError = createSelector(
  getUsersState,
  (state: UsersState) => state.error
);

export const getAllUsers = createSelector(getUsersState, (state: UsersState) =>
  selectAll(state)
);

export const getAllEnabledUsers = createSelector(getAllUsers, (users) =>
  filter(users, (u) => u.enabled)
);

export const getUsersEntities = createSelector(
  getUsersState,
  (state: UsersState) => selectEntities(state)
);

export const getSelectedId = createSelector(
  getUsersState,
  (state: UsersState) => state.selectedId
);

export const getSelectionIds = createSelector(
  getUsersState,
  (state: UsersState) => state.selectionIds
);

export const getSelected = createSelector(
  getUsersEntities,
  getSelectedId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined)
);

export const selectRoutedUser = createSelector(
  getUsersEntities,
  UserRouterSelectors.selectRoutedId,
  (entities, routedId) => (routedId ? entities[routedId] : undefined)
);

export const getSelection = createSelector(
  getUsersEntities,
  getSelectionIds,
  (entities, selectionIds) =>
    selectionIds
      ? (selectionIds
          .map((id) => entities[id])
          .filter((entity) => entity && entity.enabled) as User[])
      : ([] as User[])
);

export const getSelectionAndLoaded = createSelector(
  getSelection,
  getLoaded,
  (users, usersLoaded) => ({ users, usersLoaded })
);

export const getAllBrokers = createSelector(getAllUsers, (users) =>
  users.filter(
    (user) => user.role === Role.Broker || user.role === Role.LeadBroker
  )
);

export const selectAllEnabledBrokers = createSelector(
  getAllEnabledUsers,
  (users) =>
    users.filter(
      (user) => user.role === Role.Broker || user.role === Role.LeadBroker
    )
);

export const getUser = createSelector(
  getUsersEntities,
  AuthSelectors.getNameId,
  (entities, id) => id && entities[id]
);
